import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useGlobal } from "../../global/user";
import {
  InputNumber,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Layout,
  Row,
  Col,
  message,
  Switch,
  Radio,
  Spin,
  Alert,
} from "antd";
import { ReloadOutlined, CopyOutlined } from "@ant-design/icons";
import { FcVideoCall, FcGlobe, FcSettings, FcAlarmClock } from "react-icons/fc";
import axios from "axios";
import VideoJS from "./VideoJS";
import Canvas from "./canvas";
import "./function.css";
import { server, p2pStream, rtmpIngestBase } from "../../api";
import { Content } from "antd/lib/layout/layout";
import Map from "../Map/index";
import Gb28181Fields from "./Gb28181Fields";
import { gbRetryAction, GB28181_PROTOCOL } from "./streamRetryPolicy";

// Aspect-ratio handling for the video stage. The stage drives its own height
// from this ratio, so the line overlay stays registered with the frame in every
// feed state (loading / active / unavailable).
const FALLBACK_ASPECT = 4 / 3;
const ASPECT_MIN = 3 / 4; // tallest allowed (portrait guard)
const ASPECT_MAX = 21 / 9; // widest allowed (ultra-wide guard)
const clampAspect = (r) => Math.min(ASPECT_MAX, Math.max(ASPECT_MIN, r));

// Short status text keyed by *why* the feed is unavailable (backend `live`
// field / 403). Shown in the toolbar status label — the in-box placeholder was
// removed in favour of an elevated status label.
const UNAVAILABLE_COPY = {
  disabled: "串流已關閉",
  idle: "未推流",
  error: "連線失敗",
};

// Auto-retry cadence (seconds) while the modal is open and the feed is not yet
// active. Short enough that the ~5-8s RTMP HLS warm-up resolves hands-free, and
// surfaced as a live countdown in the status label so the user can see it work.
const RETRY_INTERVAL_S = 3;

// Quick reachability probe of the HLS playlist. Authoritative for RTMP, whose
// backend `live` is derived from a *cached* nginx /stat metric that lags and
// under-reports — a healthy RTMP stream frequently reports live:"idle" while its
// .m3u8 is already serving. A single short GET keeps the down-path fast.
const probeHls = async (url) => {
  try {
    await axios.get(url, {
      timeout: 3000,
      validateStatus: (status) => status === 200,
    });
    return true;
  } catch (error) {
    return false;
  }
};

export default function Function3({ machineID }) {
  const { user } = useGlobal();

  // UI-specific modal states
  const [videoModal, setOnVideoModal] = useState(false);
  const [durationModal, setDurationModal] = useState(false);
  const [editModal, setOnEditModal] = useState(false);
  const [mapModal, setOnMapModal] = useState(false);

  // Derived/computed states
  const [numericalValue, setNumericalValue] = useState([0, 0, 0]);
  const [draw1, setDraw1] = useState(null);
  const [draw2, setDraw2] = useState(null);
  const [showDrawLines, setShowDrawLines] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hlsUrl, setHlsUrl] = useState(null);
  // Last-frame thumbnail URL (relay-served) shown as the box backdrop when the
  // feed is down. posterLoaded gates the offline notice so a 404 (never-streamed
  // camera) cleanly falls back to the dark box.
  const [posterUrl, setPosterUrl] = useState(null);
  const [posterLoaded, setPosterLoaded] = useState(false);
  // Bumped on each startStream() (modal open / 重新整理) to cache-bust the poster
  // src, so a stale browser-cached thumbnail is re-fetched instead of staying
  // frozen until a full page reload.
  const [posterBust, setPosterBust] = useState(0);
  // Feed-layer state machine: "idle" | "loading" | "active" | "unavailable".
  // The modal and the line overlay are decoupled from this — only the bottom
  // feed layer changes; the line renders in every state.
  const [streamStatus, setStreamStatus] = useState("idle");
  // Adopted stream aspect ratio (width/height) or null → 4/3 fallback.
  const [streamAspect, setStreamAspect] = useState(null);
  // Why the feed is unavailable (key into UNAVAILABLE_COPY): disabled|idle|error.
  const [unavailableReason, setUnavailableReason] = useState(null);
  // Seconds until the next auto-retry, shown in the status label so the user can
  // see the background retry working (and need not click 重新整理).
  const [retryIn, setRetryIn] = useState(RETRY_INTERVAL_S);
  // True while a background 28181 retry's viewer/start is in flight (WVP may
  // block ~35s). Reads as 連線中… exactly like the initial start; the countdown
  // only runs between attempts.
  const [gbAttempting, setGbAttempting] = useState(false);
  // Button-loading is derived from the feed state, not a separate flag.
  const streamStarting = streamStatus === "loading" || gbAttempting;

  // Protocol selector state for the edit modal (decoupled from saved formData)
  const [protocol, setProtocol] = useState("rtmp");

  // Consolidated form data object matching database schema
  const [formData, setFormData] = useState({
    id: null,
    field_mobile_id: null,
    image: null,
    streamKey: "cartoon",
    stream_protocol: "rtmp",
    p2p_serial: null,
    p2p_username: null,
    p2p_password: null,
    p2p_host: "127.0.0.1",
    p2p_port: 1554,
    p2p_auth_type: 0,
    latitude: "25.020329",
    longitude: "121.529085",
    aiModule: 0,
    active: 0,
    notifyTime: 0,
    time: null,
    p2p_last_connect: null,
    p2p_status: "offline",
    gb28181_device_id: null,
    gb28181_channel_id: null,
  });

  // Session ID for viewer tracking (persists for component lifetime)
  const [sessionId] = useState(() => {
    return `frontend_${machineID}_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  });

  // Stable video URL - only updates when modal opens, not during refresh/stop
  const videoUrlRef = useRef(null);

  // Tracks component mount state to avoid setState-after-unmount during the
  // async viewer/start request and the liveness poll in startStream().
  const isMountedRef = useRef(true);
  // Tracks whether viewer/start has been called but not yet completed; the
  // unmount cleanup uses this to release the server-side viewer slot.
  const streamStartingRef = useRef(false);
  // True once a viewer/start response has come back. Close-cleanup releases the
  // server-side viewer slot based on this (not on whether the stream went
  // active), so an `unavailable` session that still bumped the viewer count is
  // released. The registry is cosmetic (300s TTL) so this is tidy-up, not
  // correctness-critical.
  const viewerStartedRef = useRef(false);
  // Timestamp (ms) until which auto-promotion is suppressed because the PLAYER
  // just failed to play the stream (stalled / errored). The player is the only
  // component that truly knows playability — a stale-but-200 playlist or a
  // lying `live:"streaming"` from /stat can otherwise flap the UI back to 串流中.
  // Cleared on a user-initiated 重新整理 / re-open.
  const playerFailedUntilRef = useRef(0);
  // Warn-once-per-direction tracker for the protocol selector toast.
  const warnedRef = useRef({ rtmp: false, p2p: false, gb28181: false });
  // Latest known stream protocol. `state` responses carry it but `viewer/start`
  // responses do not, so resolveLiveState / the health poll read it from here.
  const protocolRef = useRef("rtmp");

  // Component unmount cleanup: mark unmounted + release viewer slot if a
  // viewer/start is still in flight when the user navigates away.
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (streamStartingRef.current || viewerStartedRef.current) {
        fetch(`${server}v1/streaming/viewer/stop/${machineID}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Session-ID": sessionId,
          },
          body: JSON.stringify({}),
          keepalive: true,
        }).catch(() => {});
      }
    };
  }, [machineID, sessionId]);

  // Update video URL ref when modal opens (lock URL for the entire modal session)
  useEffect(() => {
    if (videoModal) {
      // Only set URL when modal first opens (ref is null or modal was closed)
      if (!videoUrlRef.current) {
        videoUrlRef.current = hlsUrl;
        console.log("Video modal opened, locked URL:", videoUrlRef.current);
      }
    } else {
      // Clear ref when modal closes
      videoUrlRef.current = null;
    }
  }, [videoModal, hlsUrl]);

  // Track previous modal state to detect actual close events
  const prevVideoModalRef = useRef(videoModal);

  // Cleanup: Decrement viewer count + reset feed state when modal closes
  useEffect(() => {
    const wasOpen = prevVideoModalRef.current;
    const isNowClosed = !videoModal;

    // Only trigger cleanup when modal actually transitions from open to closed
    if (wasOpen && isNowClosed) {
      // Release the server-side viewer slot if we ever started one — even when
      // we ended in `unavailable` (viewer/start increments immediately and is
      // independent of HLS readiness).
      if (viewerStartedRef.current) {
        console.log(
          "Video modal closed, decrementing viewer count for session:",
          sessionId,
        );
        axios
          .post(
            `${server}v1/streaming/viewer/stop/${machineID}`,
            {},
            { headers: { "X-Session-ID": sessionId } },
          )
          .then((response) => {
            console.log("Viewer removed:", response.data);
          })
          .catch((error) => {
            console.error("Error removing viewer:", error);
            // Don't show error to user - this is cleanup
          });
      }

      // Reset feed state so a reopen starts from the 4/3 loading fallback.
      viewerStartedRef.current = false;
      setStreamStatus("idle");
      setStreamAspect(null);
      setUnavailableReason(null);
      setHlsUrl(null);
    }

    // Update ref for next render
    prevVideoModalRef.current = videoModal;
  }, [videoModal, machineID, sessionId]);

  // Cleanup: Handle browser/tab close while video modal is open
  useEffect(() => {
    let cleanupSent = false;

    const sendCleanup = () => {
      if (cleanupSent) return; // Prevent duplicate sends
      if (!videoModal || !viewerStartedRef.current) return;

      cleanupSent = true;
      console.log(
        "Browser/tab closing, decrementing viewer count for session:",
        sessionId,
      );

      // Use fetch with keepalive flag for reliable delivery during page unload
      fetch(`${server}v1/streaming/viewer/stop/${machineID}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Session-ID": sessionId,
        },
        body: JSON.stringify({}),
        keepalive: true, // Keeps request alive after page unload
      }).catch((err) => console.error("Error in page unload cleanup:", err));
    };

    // Handle both beforeunload (desktop) and pagehide (mobile/iOS)
    window.addEventListener("beforeunload", sendCleanup);
    window.addEventListener("pagehide", sendCleanup);

    // Cleanup: remove listeners when component unmounts or dependencies change
    return () => {
      window.removeEventListener("beforeunload", sendCleanup);
      window.removeEventListener("pagehide", sendCleanup);
    };
  }, [videoModal, machineID, sessionId]);

  useEffect(() => {
    axios.get(`${server}v1/streaming/info/${machineID}`).then((response) => {
      console.log(response.data);
      const data = response.data;

      // Update formData with all fields from API response
      setFormData({
        id: data.id,
        field_mobile_id: data.field_mobile_id,
        image: data.image,
        streamKey: data.streamKey || "cartoon",
        stream_protocol: data.stream_protocol || "rtmp",
        p2p_serial: data.p2p_serial,
        p2p_username: data.p2p_username,
        p2p_password: data.p2p_password,
        p2p_host: data.p2p_host || "127.0.0.1",
        p2p_port: data.p2p_port || 1554,
        p2p_auth_type:
          data.p2p_auth_type !== undefined ? data.p2p_auth_type : 0,
        latitude: data.latitude || "25.020329",
        longitude: data.longitude || "121.529085",
        aiModule: data.aiModule || 0,
        active: data.active || 0,
        notifyTime: data.notifyTime || 0,
        time: data.time,
        p2p_last_connect: data.p2p_last_connect,
        p2p_status: data.p2p_status || "offline",
        gb28181_device_id: data.gb28181_device_id,
        gb28181_channel_id: data.gb28181_channel_id,
      });

      protocolRef.current = data.stream_protocol || "rtmp";

      // Sync protocol selector state from loaded value. Load-bearing for GB28181:
      // without a matching radio (added below) a gb28181 row would render with no
      // protocol selected, and touching the selector would clobber it on save.
      if (data.stream_protocol) {
        setProtocol(data.stream_protocol);
      }

      // Update draw states separately (parse JSON if needed)
      if (data.draw1) {
        setDraw1(
          typeof data.draw1 === "string" ? JSON.parse(data.draw1) : data.draw1,
        );
      }
      if (data.draw2) {
        setDraw2(
          typeof data.draw2 === "string" ? JSON.parse(data.draw2) : data.draw2,
        );
      }
    });
  }, [machineID]);

  // Check for active stream on mount via unified state endpoint. This only
  // pre-seeds hlsUrl so the button title reflects an already-live stream; the
  // modal feed state is driven by startStream()/the poll once opened.
  useEffect(() => {
    axios
      .get(`${server}v1/streaming/state/${machineID}`)
      .then((response) => {
        const data = response.data;
        setPosterUrl(data.last_frame_url || null);
        if (data.live === "streaming" && data.hls_url) {
          console.log("Active stream detected:", data);
          setHlsUrl(data.hls_url);
        } else {
          setHlsUrl(null);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch stream state:", error);
      });
  }, [machineID]);

  const [form] = Form.useForm();
  const [durationForm] = Form.useForm();
  const watchedStreamKey = Form.useWatch("streamKey", form) ?? "";
  // GB28181 has no push/ingest URL (cameras register inbound via SIP), so it gets
  // no address here — and the row it renders is hidden for gb28181 below. Explicit
  // null rather than falling into the p2p branch, which would show a wrong URL.
  const streamAddress =
    protocol === "rtmp"
      ? `${rtmpIngestBase}${watchedStreamKey}`
      : protocol === "p2p"
        ? `${p2pStream}streaming/${watchedStreamKey}.m3u8`
        : null;
  const handleCopyStreamAddress = async () => {
    try {
      await navigator.clipboard.writeText(streamAddress);
      message.success("已複製串流地址");
    } catch (err) {
      message.error("複製失敗，請手動複製");
    }
  };
  const rules = [
    {
      required: true,
      message: "不可為空",
    },
  ];

  function rotatePoint(center, point, angle) {
    const angleRad = angle * (Math.PI / 180); // Convert angle to radians

    const x =
      center[0] +
      (point[0] - center[0]) * Math.cos(angleRad) -
      (point[1] - center[1]) * Math.sin(angleRad);
    const y =
      center[1] +
      (point[0] - center[0]) * Math.sin(angleRad) +
      (point[1] - center[1]) * Math.cos(angleRad);

    return [x, y];
  }

  function findMidpointFromCoordinates(arr) {
    if (!arr || arr.length === 0) {
      return null;
    }

    const sum = arr.reduce(
      (acc, val) => {
        return [acc[0] + val[0], acc[1] + val[1]];
      },
      [0, 0],
    );

    return [sum[0] / arr.length, sum[1] / arr.length];
  }

  function findArrowPoints(arr, c, d) {
    const midpoint = findMidpointFromCoordinates(arr);
    console.log("points", arr);
    console.log("midpoint", midpoint);
    var angle = 0.0;
    const middleIndex = Math.floor(arr.length / 2);
    if (arr.length % 2 === 0) {
      const idx = middleIndex;
      angle = Math.atan2(
        arr[idx + 1][1] - arr[idx][1],
        arr[idx + 1][0] - arr[idx][0],
      );
      if (angle < 0) {
        angle += Math.PI / 2;
      } else {
        angle -= Math.PI / 2;
      }
    } else {
      const idx = Math.floor(arr.length / 2) + 1;
      angle = Math.atan2(midpoint[1] - arr[idx][1], midpoint[0] - arr[idx][0]);
    }
    console.log("angle", angle);

    const p1 = [
      midpoint[0] + d * Math.cos(angle),
      midpoint[1] + d * Math.sin(angle),
    ];
    const p2 = [
      midpoint[0] - d * Math.cos(angle),
      midpoint[1] - d * Math.sin(angle),
    ];

    const points = [p1, p2];

    if (c === 1 || c === 2) {
      points.push(rotatePoint(p1, p2, 60));
      points.push(rotatePoint(p1, p2, 300));
    }

    if (c === 0 || c === 2) {
      points.push(rotatePoint(p2, p1, 60));
      points.push(rotatePoint(p2, p1, 300));
    }

    return points;
  }

  // Memoized so the same `options` object is reused across renders. VideoJS
  // re-runs its init effect — disposing and recreating the player — whenever
  // `options` changes identity, so this must stay stable. Recomputes only when
  // the source URL actually changes. (Defining the player inline inside a
  // nested component instead remounts it on every parent render, which is the
  // dispose/recreate loop we deliberately avoid here.)
  const videoJsOptions = useMemo(
    () => ({
      autoplay: true,
      controls: true,
      responsive: true,
      // Fill the feed layer (absolute inset:0) instead of computing its own
      // aspect padding — the .video-stage owns the box, so a fluid player would
      // double-letterbox against it.
      fill: true,
      sources: [
        {
          src: videoUrlRef.current || hlsUrl,
          type: "application/x-mpegURL",
        },
      ],
      html5: {
        vhs: {
          withCredentials: false,
          enableLowInitialPlaylist: true,
          smoothQualityChange: true,
          overrideNative: true,
          // Increase timeout and retry attempts for unstable streams
          experimentalBufferBasedABR: true,
        },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
      // Increase tolerance for errors
      liveui: true,
      liveTracker: {
        trackingThreshold: 0,
        liveTolerance: 15,
      },
    }),
    [hlsUrl],
  );

  const draw = useCallback(
    (context) => {
      // Capture refreshKey in closure to trigger re-renders when refresh is clicked
      void refreshKey; // Ensure refreshKey is captured in closure

      // Layout size (clientWidth/Height), NOT getBoundingClientRect(): the latter
      // is transform-scaled, so during the modal's open zoom it returns a transient
      // size and the lines render at an inconsistent scale. clientWidth matches the
      // backing buffer sized in canvas.js and is transform-immune.
      const cssW = context.canvas.clientWidth;
      const cssH = context.canvas.clientHeight;

      // Clear canvas using CSS dimensions (because context is scaled by DPR)
      context.clearRect(0, 0, cssW, cssH);

      // Only draw if showDrawLines is enabled and we have data
      if (!showDrawLines || (!draw1 && !draw2)) return;

      // Calculate scale factors based on display size
      const xScaleFactor = cssW / parseFloat(8191);
      const yScaleFactor = cssH / parseFloat(8191);

      // Set line rendering quality (makes lines smoother)
      context.lineWidth = 2;
      context.lineCap = "round";
      context.lineJoin = "round";

      // Draw first path
      if (draw1 && draw1.coordinates) {
        context.beginPath();
        draw1.coordinates.forEach((coordinate, index) => {
          var scaleX = coordinate[0] * xScaleFactor;
          var scaleY = coordinate[1] * yScaleFactor;
          if (index === 0) {
            context.moveTo(scaleX, scaleY);
          } else {
            context.lineTo(scaleX, scaleY);
          }
        });
        context.strokeStyle = "#FF359A";
        context.stroke();
      }

      // Draw second path
      if (draw2 && draw2.coordinates) {
        context.beginPath();
        draw2.coordinates.forEach((coordinate, index) => {
          var scaleX = coordinate[0] * xScaleFactor;
          var scaleY = coordinate[1] * yScaleFactor;
          if (index === 0) {
            context.moveTo(scaleX, scaleY);
          } else {
            context.lineTo(scaleX, scaleY);
          }
        });
        context.strokeStyle = "#8600FF";
        context.stroke();
      }
    },
    [refreshKey, showDrawLines, draw1, draw2],
  );

  function handleDurationSubmit({ hour, minute, second }) {
    const notifyTime = hour * 3600 + minute * 60 + second;
    setFormData((prev) => ({ ...prev, notifyTime }));
    axios.post(server + "v1/streaming/duration", {
      total: notifyTime,
      deviceID: machineID,
    });
  }

  // Helper function to convert seconds to hours, minutes, seconds
  const getTimeFromSeconds = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return { hour: hours, minute: minutes, second: secs };
  };

  const handleEditSubmit = async (fields) => {
    try {
      await axios.post(server + "v1/streaming/info", {
        ...fields,
        deviceID: machineID,
        stream_protocol: protocol,
      });
      protocolRef.current = protocol;
      // Only mirror into local state once the server has persisted the change,
      // so the UI can't show a stale "saved" value after a failed request.
      setFormData((prev) => ({
        ...prev,
        ...fields,
        stream_protocol: protocol,
      }));
      message.success("設定已儲存");
    } catch (error) {
      console.error("Error saving streaming info:", error);
      message.error(error.response?.data?.message || "儲存失敗，請稍後再試");
    }
  };

  // Resolve the backend `live` field (returned by both viewer/start and state)
  // onto the feed-layer state machine. `live` is a fast-path; when it is not yet
  // streaming but an hls_url exists, the playlist probe is the real gate (this
  // is the RTMP case above, and also catches "came up but /stat not refreshed").
  const resolveLiveState = useCallback(async (data) => {
    if (!isMountedRef.current) return;
    if (data.protocol) protocolRef.current = data.protocol;
    // Last-frame poster is independent of live state — a down camera still has
    // one. Capture it on every resolve (viewer/start + state poll).
    setPosterUrl(data.last_frame_url || null);
    const live = data.live;
    const url = data.hls_url;

    if (live === "streaming" && url) {
      setHlsUrl(url);
      setUnavailableReason(null);
      setStreamStatus("active");
      return;
    }
    if (live === "starting") {
      // P2P warm-up — keep loading; the poll promotes to active when ready.
      setStreamStatus("loading");
      return;
    }
    if (data.active === 0) {
      // Camera disabled (state reports active=0).
      setUnavailableReason("disabled");
      setStreamStatus("unavailable");
      return;
    }
    // The player recently failed to play this stream — do NOT remount it (no
    // matter what `live`/the probe say) until the cooldown elapses or the user
    // forces a retry. Stops a stale playlist / lying /stat from flapping us back
    // to active over a feed the player already proved it can't play.
    if (Date.now() < playerFailedUntilRef.current) {
      setUnavailableReason("error");
      setStreamStatus("unavailable");
      return;
    }
    // live is idle/error but the playlist may still be live (stale /stat). Probe
    // it before declaring the feed offline. 28181 trusts `live`: WVP/ZLM state is
    // authoritative and a probe would only race its start-on-demand.
    if (url && protocolRef.current !== GB28181_PROTOCOL) {
      const ok = await probeHls(url);
      if (!isMountedRef.current) return;
      if (ok) {
        setHlsUrl(url);
        setUnavailableReason(null);
        setStreamStatus("active");
        return;
      }
    }
    setUnavailableReason(live === "error" ? "error" : "idle");
    setStreamStatus("unavailable");
  }, []);

  // Start (or re-attempt) the stream. Reused by the modal-open handler, the
  // 重新整理 toolbar button, and the 重試 placeholder button. Display-only: it
  // re-triggers the pull (P2P) / re-checks RTMP liveness, but never re-activates
  // a guard-disabled camera (POST /streaming/active lives elsewhere).
  const startStream = useCallback(async () => {
    // User-initiated (modal open / 重新整理) → clear any player-failure cooldown
    // so we attempt a fresh mount immediately.
    playerFailedUntilRef.current = 0;
    // Cache-bust the last-frame poster so reopening the modal re-fetches the latest
    // thumbnail rather than the browser-cached one (which otherwise stays until a
    // page reload). Reset posterLoaded so the offline notice waits for the reload.
    setPosterBust((n) => n + 1);
    setPosterLoaded(false);
    streamStartingRef.current = true;
    setStreamStatus("loading");
    setUnavailableReason(null);
    try {
      const startResponse = await axios.post(
        `${server}v1/streaming/viewer/start/${machineID}`,
        {},
        { headers: { "X-Session-ID": sessionId } },
      );
      if (!isMountedRef.current) return;
      // Mark started only on a non-throwing response (403 stays unstarted, so we
      // don't try to release a slot we never took).
      viewerStartedRef.current = true;
      console.log("Viewer start response:", startResponse.data);
      await resolveLiveState(startResponse.data);
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error("Error starting viewer:", error);
      setUnavailableReason(
        error.response?.status === 403 ? "disabled" : "error",
      );
      setStreamStatus("unavailable");
    } finally {
      streamStartingRef.current = false;
    }
  }, [machineID, sessionId, resolveLiveState]);

  // video.js wiring. The player is the authority on *playability*: it demotes
  // the feed on two signals the backend can't be trusted for —
  //   (1) a *persisted* fatal error (debounced: live HLS emits a transient error
  //       during initial buffering and VideoJS retries network errors itself, so
  //       we wait DEMOTE_GRACE_MS and cancel if `playing` fires), and
  //   (2) a *playback stall* — currentTime not advancing while not paused. This
  //       is the case VHS will NEVER surface as an error: when the playlist 200s
  //       but its segments 404 (stale playlist) or the stream dies mid-play, VHS
  //       just logs "Problem encountered with playlist… trying again" forever and
  //       the UI would otherwise sit on 串流中 over a frozen/black frame.
  // Either failure arms playerFailedUntilRef so we don't immediately remount the
  // same unplayable stream (see resolveLiveState cooldown gate).
  const DEMOTE_GRACE_MS = 8000;
  const PLAYER_STALL_MS = 12000;
  const PLAYER_FAIL_COOLDOWN_MS = 15000;
  const handlePlayerReady = useCallback((player) => {
    let demoteTimer = null;
    let lastTime = -1;
    let lastProgressAt = Date.now();
    const cancelDemote = () => {
      if (demoteTimer) {
        clearTimeout(demoteTimer);
        demoteTimer = null;
      }
    };
    const isGone = () =>
      !isMountedRef.current ||
      (typeof player.isDisposed === "function" && player.isDisposed());
    const failFeed = (reason) => {
      if (isGone()) return;
      playerFailedUntilRef.current = Date.now() + PLAYER_FAIL_COOLDOWN_MS;
      setUnavailableReason(reason);
      setStreamStatus("unavailable");
    };

    player.on("loadedmetadata", () => {
      if (!isMountedRef.current) return;
      const w = player.videoWidth();
      const h = player.videoHeight();
      if (w && h) setStreamAspect(clampAspect(w / h));
    });
    // Real playback progress → the feed is healthy; reset both watchdogs.
    player.on("playing", () => {
      cancelDemote();
      lastProgressAt = Date.now();
    });
    player.on("error", () => {
      if (!isMountedRef.current) return;
      cancelDemote();
      demoteTimer = setTimeout(() => {
        demoteTimer = null;
        if (isGone()) return;
        console.error(
          "VideoJS player error persisted, demoting feed to unavailable",
        );
        failFeed("error");
      }, DEMOTE_GRACE_MS);
    });

    // Stall watchdog: if currentTime stops advancing (segments 404 / stream
    // died) while not paused, the feed is dead even though VHS never errors.
    const stallTimer = setInterval(() => {
      if (isGone()) return;
      if (player.paused()) {
        lastProgressAt = Date.now(); // a user pause is not a stall
        return;
      }
      const t = player.currentTime() || 0;
      if (t > lastTime + 0.25) {
        lastTime = t;
        lastProgressAt = Date.now();
      } else if (Date.now() - lastProgressAt > PLAYER_STALL_MS) {
        console.warn(
          "VideoJS playback stalled (no progress), demoting feed to unavailable",
        );
        failFeed("error");
      }
    }, 2000);

    player.on("dispose", () => {
      cancelDemote();
      clearInterval(stallTimer);
    });
  }, []);

  // Bounded liveness poll: while the modal is open and the feed is NOT active,
  // poll `state` and let resolveLiveState promote it (via `live: streaming` or a
  // successful playlist probe). This makes "stream comes up later" (P2P warm-up,
  // camera re-published, /stat catching up) self-healing and demotes the manual
  // retry to a fallback. A genuinely down stream 404s the probe and stays
  // offline (no flap). We stop polling once active; death-while-active is handled
  // by the player `error` event above.
  //
  // 28181 differs only below the UI: WVP pulls on a play request, so when the
  // policy says "start" the tick issues a lightweight viewer/start (no cooldown
  // reset, no poster bust — unlike startStream). An attempt can block ~35s, so
  // attempts are sequential: the ticker/countdown pauses while one is in flight.
  useEffect(() => {
    if (!videoModal || streamStatus === "active") return;
    let cancelled = false;
    let busy = false; // skip a probe while a previous resolve/probe is in flight
    let attempting = false; // 28181 viewer/start in flight → pause the ticker
    let countdown = RETRY_INTERVAL_S;
    setRetryIn(RETRY_INTERVAL_S);

    const startAttempt = async () => {
      attempting = true;
      streamStartingRef.current = true; // unmount releases the viewer slot
      setGbAttempting(true);
      try {
        const response = await axios.post(
          `${server}v1/streaming/viewer/start/${machineID}`,
          {},
          { headers: { "X-Session-ID": sessionId } },
        );
        if (!isMountedRef.current) return;
        viewerStartedRef.current = true;
        if (cancelled) return;
        await resolveLiveState(response.data);
      } catch (error) {
        if (!isMountedRef.current || cancelled) return;
        if (error.response?.status === 403) {
          setUnavailableReason("disabled");
          setStreamStatus("unavailable");
        } else {
          // Transient start failure — keep the current state, retry next tick.
          console.error("Stream start retry failed:", error);
        }
      } finally {
        attempting = false;
        streamStartingRef.current = false;
        countdown = RETRY_INTERVAL_S;
        if (isMountedRef.current) {
          setGbAttempting(false);
          setRetryIn(RETRY_INTERVAL_S);
        }
      }
    };

    const poll = async () => {
      if (busy || cancelled) return;
      // The initial startStream() owns the connection while it is loading.
      if (
        streamStatus === "loading" &&
        protocolRef.current === GB28181_PROTOCOL
      ) {
        return;
      }
      busy = true;
      try {
        const response = await axios.get(
          `${server}v1/streaming/state/${machineID}`,
        );
        if (cancelled || !isMountedRef.current) return;
        if (response.data.protocol)
          protocolRef.current = response.data.protocol;
        const action = gbRetryAction(response.data, {
          inCooldown: Date.now() < playerFailedUntilRef.current,
        });
        if (action === "start") {
          await startAttempt();
        } else {
          await resolveLiveState(response.data);
        }
      } catch (error) {
        // Transient poll failure — keep the current state, try again next tick.
        console.error("Stream state poll failed:", error);
      } finally {
        busy = false;
      }
    };

    // 1s ticker drives the visible countdown; fire the actual poll/probe when it
    // wraps. This both surfaces "next retry in Ns" and paces the auto-retry.
    const ticker = setInterval(() => {
      if (cancelled || attempting) return;
      countdown -= 1;
      if (countdown <= 0) {
        countdown = RETRY_INTERVAL_S;
        poll();
      }
      setRetryIn(countdown);
    }, 1000);

    return () => {
      cancelled = true;
      clearInterval(ticker);
    };
  }, [videoModal, streamStatus, machineID, sessionId, resolveLiveState]);

  // Health poll while the feed is ACTIVE. A live stream can die underneath the
  // player at any time — the guard's Tier-2 300s hard cap and Tier-1 idle kill
  // both set `active=0` (sticky), and a dropped camera 404s the playlist. On
  // none of these does video.js fire a fatal `error` (it just "waits for stream
  // data" forever), so without this the UI stalls on 串流中 over a frozen frame.
  // resolveLiveState keeps it active while genuinely live (active=1 + playlist
  // reachable) and demotes on active=0 (→ 串流已關閉) / playlist gone; `active=0`
  // short-circuits before the probe, so a stale 200 can't flap it back.
  useEffect(() => {
    if (!videoModal || streamStatus !== "active") return;
    let cancelled = false;
    let busy = false;
    let probeFails = 0;
    const MAX_PROBE_FAILS = 2; // tolerate transient blips before demoting
    const id = setInterval(async () => {
      if (busy || cancelled) return;
      busy = true;
      try {
        const { data } = await axios.get(
          `${server}v1/streaming/state/${machineID}`,
        );
        if (cancelled || !isMountedRef.current) return;
        if (data.protocol) protocolRef.current = data.protocol;

        // Definitive: the guard closed the gate (Tier-1/Tier-2 → active=0,
        // sticky). Demote at once to 串流已關閉 — no probe, no flap.
        if (data.active === 0) {
          setUnavailableReason("disabled");
          setStreamStatus("unavailable");
          return;
        }
        // Fresh positive liveness from /stat — definitely still up.
        if (data.live === "streaming") {
          probeFails = 0;
          return;
        }
        // live idle/error while still enabled: confirm via the playlist, but
        // tolerate a couple of transient probe failures so a network blip does
        // not knock a healthy, playing feed offline. 28181 trusts `live`, so a
        // non-streaming tick counts as a strike without probing.
        const ok =
          data.hls_url && protocolRef.current !== GB28181_PROTOCOL
            ? await probeHls(data.hls_url)
            : false;
        if (cancelled || !isMountedRef.current) return;
        if (ok) {
          probeFails = 0;
        } else if (++probeFails >= MAX_PROBE_FAILS) {
          setUnavailableReason("idle");
          setStreamStatus("unavailable");
        }
      } catch (error) {
        // Transient failure — don't demote a healthy feed on one bad request.
        console.error("Active health poll failed:", error);
      } finally {
        busy = false;
      }
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [videoModal, streamStatus, machineID]);

  const handleVideoButtonClick = () => {
    // Decoupled open: the modal (and the line overlay) open immediately; the
    // feed state is driven asynchronously by startStream(). The line renders
    // whether or not a feed ever comes up.
    setOnVideoModal(true);
    startStream();
  };

  // Toolbar status label (replaces the in-box placeholder). A colored dot plus
  // short text tells the user whether the feed is up, connecting, auto-retrying
  // (with the live countdown), or disabled.
  const streamStatusLabel = (() => {
    if (streamStatus === "active") {
      return { color: "#52c41a", text: "串流中" }; // green — up
    }
    if (streamStatus === "loading" || gbAttempting) {
      return { color: "#faad14", text: "連線中…" }; // amber — initial start / 28181 retry
    }
    if (unavailableReason === "disabled") {
      // Red, no retry text — needs an admin re-enable, not a reload.
      return { color: "#ff4d4f", text: "串流已關閉" };
    }
    // idle / error → still auto-retrying; show the countdown.
    const base = UNAVAILABLE_COPY[unavailableReason] || "離線";
    return { color: "#faad14", text: `${base}・自動重試中 (${retryIn}s)` };
  })();

  return (
    <React.Fragment>
      <td>
        <div className="d-flex justify-content-center py-2">
          <div style={{ width: "60%" }}>
            <div className="d-flex justify-content-center mb-3">
              <div>
                <span className="font-weight-bold mr-1">{`編號:`}</span>
                <span className="mr-2 text-nowrap">{`${machineID}`}</span>
              </div>
            </div>
            <div className="d-flex justify-content-center mb-3">
              <Button
                shape="circle"
                style={{ marginRight: 3 }}
                onClick={handleVideoButtonClick}
                icon={<FcVideoCall />}
                loading={streamStarting}
                disabled={streamStarting}
                title={
                  streamStarting
                    ? "正在啟動串流..."
                    : hlsUrl
                      ? "觀看即時影像"
                      : "啟動串流並觀看"
                }
              ></Button>
              <Button
                shape="circle"
                style={{ marginRight: 3 }}
                onClick={() => {
                  setOnMapModal(true);
                }}
                icon={<FcGlobe />}
                title="查看設備位置"
              ></Button>
              {user.class >= process.env.REACT_APP_EDITOR_CALSS_LV && (
                <>
                  <Button
                    shape="circle"
                    style={{ marginRight: 3 }}
                    icon={<FcSettings />}
                    onClick={() => setOnEditModal(true)}
                  ></Button>
                  <Button
                    shape="circle"
                    style={{ marginRight: 3 }}
                    icon={<FcAlarmClock />}
                    onClick={() => setDurationModal(true)}
                  ></Button>
                </>
              )}
            </div>
          </div>
        </div>
        <div>
          <Modal
            open={editModal}
            onCancel={() => setOnEditModal(false)}
            title="編輯影像內容"
            width={720}
            afterOpenChange={(open) => {
              if (open) {
                // Reset per-modal-session warning tracker
                warnedRef.current = { rtmp: false, p2p: false, gb28181: false };
              }
            }}
            onOk={() => {
              form.submit();
              setOnEditModal(false);
            }}
          >
            <Form form={form} onFinish={handleEditSubmit} autoComplete="off">
              <Form.Item label="協定">
                <Radio.Group
                  value={protocol}
                  onChange={(e) => {
                    const next = e.target.value;
                    // Warn once per destination per modal session, and only when a
                    // DIFFERENT protocol still holds real (non-default) config —
                    // switching leaves that config saved but inert, which surprises
                    // operators. Generalized from the old rtmp<->p2p pair to include
                    // gb28181.
                    const realConfig = {
                      rtmp:
                        Boolean(formData.streamKey) &&
                        formData.streamKey !== "cartoon",
                      p2p: Boolean(formData.p2p_serial),
                      gb28181: Boolean(formData.gb28181_device_id),
                    };
                    const otherHasConfig = Object.keys(realConfig).some(
                      (p) => p !== next && realConfig[p],
                    );
                    if (otherHasConfig && !warnedRef.current[next]) {
                      warnedRef.current[next] = true;
                      message.warning(
                        "切換協定後，其他協定的設定值仍保留但不會生效。",
                      );
                    }
                    setProtocol(next);
                  }}
                >
                  <Radio value="rtmp">RTMP</Radio>
                  <Radio value="p2p">P2P</Radio>
                  <Radio value="gb28181">28181</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                label="串流金鑰"
                name="streamKey"
                // GB28181 uses device/channel ids, not a stream key; keeping the
                // required rule would block saving those ids (the DB default
                // "cartoon" only masks this for pre-existing rows).
                rules={protocol === "gb28181" ? [] : rules}
                initialValue={formData.streamKey}
              >
                <Input
                  placeholder="例如：cartoon"
                  disabled={protocol === "gb28181"}
                />
              </Form.Item>
              {protocol !== "gb28181" && (
                <Form.Item label="串流地址">
                  <Space.Compact style={{ width: "100%" }}>
                    <Input value={streamAddress} readOnly />
                    <Button
                      icon={<CopyOutlined />}
                      onClick={handleCopyStreamAddress}
                      title="複製串流地址"
                    />
                  </Space.Compact>
                </Form.Item>
              )}
              <Space size={20}>
                <Form.Item
                  label="緯度"
                  name="latitude"
                  rules={rules}
                  initialValue={formData.latitude}
                >
                  <Input style={{ width: 120 }} />
                </Form.Item>
                <Form.Item
                  label="經度"
                  name="longitude"
                  rules={rules}
                  initialValue={formData.longitude}
                >
                  <Input style={{ width: 120 }} />
                </Form.Item>
              </Space>
              <Form.Item
                label="AI訓練模組"
                name="aiModule"
                rules={rules}
                initialValue={formData.aiModule}
              >
                <Select
                  style={{ width: 150 }}
                  options={[
                    { value: 0, label: "水位辨識v1" },
                    { value: 1, label: "鏽蝕面積" },
                    { value: 2, label: "熱影像高低溫" },
                    { value: 3, label: "電線杆傾斜" },
                    { value: 4, label: "鳥巢辨識" },
                  ]}
                />
              </Form.Item>
              {protocol === "p2p" && (
                <>
                  <Form.Item
                    label="P2P設備序列號"
                    name="p2p_serial"
                    initialValue={formData.p2p_serial}
                  >
                    <Input placeholder="選填" />
                  </Form.Item>
                  <Form.Item
                    label="P2P認證類型"
                    name="p2p_auth_type"
                    initialValue={formData.p2p_auth_type}
                  >
                    <Select
                      style={{ width: 150 }}
                      options={[
                        { value: 0, label: "無認證" },
                        { value: 1, label: "用戶名密碼" },
                      ]}
                    />
                  </Form.Item>
                  <Space size={20}>
                    <Form.Item
                      label="P2P用戶名"
                      name="p2p_username"
                      initialValue={formData.p2p_username}
                    >
                      <Input placeholder="選填" />
                    </Form.Item>
                    <Form.Item
                      label="P2P密碼"
                      name="p2p_password"
                      initialValue={formData.p2p_password}
                    >
                      <Input.Password placeholder="選填" />
                    </Form.Item>
                  </Space>
                </>
              )}
              {protocol === "gb28181" && (
                <Gb28181Fields form={form} formData={formData} />
              )}
            </Form>
          </Modal>

          <Modal
            title="設備位置"
            open={mapModal}
            onCancel={() => {
              setOnMapModal(false);
            }}
            footer={null}
            width={800}
          >
            <Layout>
              <Content>
                <Row flex={1}>
                  <Col flex="100%">
                    <Map lat={formData.latitude} lng={formData.longitude} />
                  </Col>
                </Row>
              </Content>
            </Layout>
          </Modal>

          <Modal
            title="即時影像"
            open={videoModal}
            onCancel={() => {
              setOnVideoModal(false);
            }}
            footer={null}
            // Cap the modal width from the viewport height so the stage's
            // derived height (width x aspect ratio) can never push the modal
            // past the viewport. The (100vh - 260px) budget covers the header,
            // toolbar row, body padding, and the centered top/bottom margins.
            // The aspect term MUST read the same clamped value as the stage's
            // `aspect-ratio` below so the height budget holds for any adopted
            // aspect. The 1280px / 92vw terms keep it sane on tall and narrow
            // screens respectively.
            width={`min(1280px, 92vw, calc((100vh - 260px) * ${clampAspect(
              streamAspect ?? FALLBACK_ASPECT,
            )}))`}
            centered
            destroyOnHidden={true}
          >
            {/* Draw Lines Control */}
            <div
              style={{
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "8px",
              }}
            >
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={streamStatus === "loading"}
                onClick={() => {
                  // Re-check liveness / re-trigger the pull AND redraw the line.
                  setRefreshKey((prev) => prev + 1);
                  startStream();
                }}
              >
                重新整理
              </Button>
              <span style={{ fontWeight: "bold", marginLeft: "16px" }}>
                顯示線圖:
              </span>
              <Switch
                checked={showDrawLines}
                onChange={setShowDrawLines}
                checkedChildren="開啟"
                unCheckedChildren="關閉"
              />
              {/* Elevated stream status label */}
              <span
                style={{
                  marginLeft: "auto",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: streamStatusLabel.color,
                    display: "inline-block",
                    flex: "0 0 auto",
                  }}
                />
                {streamStatusLabel.text}
              </span>
            </div>

            {/* Last-frame notice — independent banner between the operation bar
                and the stream box; shown only while the frozen last frame is the
                thing on screen (feed not live AND the poster actually loaded). */}
            {posterLoaded && streamStatus !== "active" && (
              <Alert
                type="warning"
                showIcon
                message="串流離線，顯示最後畫面"
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Video stage: self-sized box owning both the feed layer and the
                line overlay. The overlay (線圖) renders in every feed state, so
                the lines show whether or not a stream is up behind them. */}
            {videoModal && (
              <div
                className="video-stage"
                style={{
                  position: "relative",
                  width: "100%",
                  // Drives height deterministically; same clamped value as the
                  // modal width cap. Falls back to 4/3 until loadedmetadata.
                  aspectRatio: String(
                    clampAspect(streamAspect ?? FALLBACK_ASPECT),
                  ),
                  background: "#000",
                  overflow: "hidden",
                }}
              >
                {/* Feed layer (z:0) — only this changes per stream state. */}
                <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
                  {streamStatus === "active" && hlsUrl ? (
                    <VideoJS
                      options={videoJsOptions}
                      onReady={handlePlayerReady}
                    />
                  ) : (
                    // Not active: show the frozen last frame (if any) as the box
                    // background, with a spinner over it while connecting / auto-
                    // retrying. A 404 (never-streamed) hides the img -> dark box.
                    // The line overlay still renders on top.
                    <>
                      {posterUrl && (
                        <img
                          src={`${posterUrl}?b=${posterBust}`}
                          alt=""
                          onLoad={(e) => {
                            setPosterLoaded(true);
                            // Adopt the frame's real aspect — same as the video does
                            // on loadedmetadata (handlePlayerReady) — so the stage
                            // box matches the image and it fills exactly (no crop, no
                            // upscale), keeping the 線圖 overlay, which is registered
                            // to the box, aligned 1:1 just like on the live player.
                            const w = e.currentTarget.naturalWidth;
                            const h = e.currentTarget.naturalHeight;
                            if (w && h) setStreamAspect(clampAspect(w / h));
                          }}
                          onError={() => setPosterLoaded(false)}
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            // Aspect-preserving like the video; with the box aspect
                            // adopted above it fills exactly (no letterbox).
                            objectFit: "contain",
                            // Stay hidden until it actually loads: a 404 (no last
                            // frame yet) keeps posterLoaded=false so the element —
                            // and the browser's broken-image icon — never shows.
                            // display:none still lets it fetch + fire onLoad/onError.
                            display: posterLoaded ? "block" : "none",
                          }}
                        />
                      )}
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {unavailableReason !== "disabled" && <Spin />}
                      </div>
                    </>
                  )}
                </div>

                {/* Overlay layer (z:10) — the line, always mounted while open.
                    pointerEvents:none so the video controls stay clickable. */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 10,
                    pointerEvents: "none",
                  }}
                >
                  <Canvas draw={draw} />
                </div>
              </div>
            )}
          </Modal>

          <Modal
            title="設定通報時間間隔"
            open={durationModal}
            onCancel={() => setDurationModal(false)}
            onOk={() => {
              durationForm.submit();
              setDurationModal(false);
            }}
          >
            <Form
              form={durationForm}
              autoComplete="off"
              style={{ display: "flex" }}
              onFinish={handleDurationSubmit}
            >
              <Form.Item
                label="時"
                name="hour"
                initialValue={getTimeFromSeconds(formData.notifyTime).hour}
                rules={rules}
              >
                <InputNumber min={0} />
              </Form.Item>
              <Form.Item
                label="分"
                name="minute"
                initialValue={getTimeFromSeconds(formData.notifyTime).minute}
                rules={rules}
              >
                <InputNumber min={0} />
              </Form.Item>
              <Form.Item
                label="秒"
                name="second"
                initialValue={getTimeFromSeconds(formData.notifyTime).second}
                rules={rules}
              >
                <InputNumber min={0} />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </td>
    </React.Fragment>
  );
}
